import { useEffect, useState } from "react";

const WeatherComponent = () => {
  const [currentWeather, setCurrentWeather] = useState(null);
  const [setForecast] = useState([]); // ← Now used!
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLocationError("Location access denied. Using default: Islamabad");
          setLocation({ lat: 33.6844, lon: 73.0479, name: "Islamabad" });
        }
      );
    } else {
      setLocationError("Geolocation not supported. Using default: Islamabad");
      setLocation({ lat: 33.6844, lon: 73.0479, name: "Islamabad" });
    }
  }, []);

  // Fetch weather data when location is available
  useEffect(() => {
    if (!location) return;

    const fetchWeather = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

        // Current weather
        const currentRes = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${location.lat}&lon=${location.lon}&appid=${API_KEY}&units=metric`
        );
        if (!currentRes.ok) throw new Error("Failed to fetch current weather");
        const currentData = await currentRes.json();

        // Forecast
        const forecastRes = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?lat=${location.lat}&lon=${location.lon}&appid=${API_KEY}&units=metric`
        );
        if (!forecastRes.ok) throw new Error("Failed to fetch forecast");
        const forecastData = await forecastRes.json();

        setCurrentWeather(currentData);

        // Get next two days forecast (midday ~12:00)
        const dailyForecast = forecastData.list
          .filter((item) => item.dt_txt.includes("12:00:00"))
          .slice(0, 2);

        setForecast(dailyForecast); // ← Now used + in deps
        setLoading(false);
      } catch (error) {
        console.error("Error fetching weather:", error);
        setErrorMessage("Failed to load weather data");
        setLoading(false);
      }
    };

    fetchWeather();
  }, [location, setForecast]); // ← Fixed: added setForecast

  const getWeatherIcon = (condition) => {
    const icons = {
      Rain: "Rainy",
      Clear: "Sunny",
      Clouds: "Cloudy",
      Snow: "Snowy",
      Thunderstorm: "Thunderstorm",
    };
    return icons[condition] || "Partly Cloudy";
  };

  const getPlantationMessage = (temp, condition) => {
    if (condition === "Rain") {
      return "Rainy weather! Nature is watering your future forest - ideal time for planting saplings";
    }
    if (temp > 30) {
      return "Warm weather calls for extra care - plant in shade and water generously for healthy growth";
    }
    if (temp >= 25 && temp <= 30) {
      return "Marvellous temperature for tree growth! Your saplings will thrive in this nurturing climate";
    }
    return "Magnificent for plantation work - perfect conditions for outdoor tree planting activities";
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  return (
    <div className="weather-forest-container">
      <div className="weather-forest-message">
        <p className="weather-forest-message-text">
          {getPlantationMessage(
            currentWeather?.main.temp || 28,
            currentWeather?.weather[0].main || "Rain"
          )}
        </p>
      </div>

      {locationError && (
        <div className="weather-forest-error">
          {locationError}
        </div>
      )}

      {errorMessage && (
        <div className="weather-forest-error">
          Weather data temporarily unavailable: {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="weather-forest-loading">
          <div className="weather-forest-spinner"></div>
          <div className="weather-forest-loading-text">
            Growing weather insights...
          </div>
        </div>
      ) : (
        <>
          <div className="weather-forest-header">
            <div className="weather-forest-title">
              <h3 className="weather-forest-location">
                {currentWeather?.name || location?.name || "Islamabad"}
              </h3>
              <div className="weather-forest-date">
                {formatDate(currentWeather?.dt || Date.now() / 1000)}
              </div>
            </div>
          </div>

          <div className="weather-forest-main">
            <div className="weather-forest-current">
              <div className="weather-forest-temp-section">
                <div className="weather-forest-temp">
                  {Math.round(currentWeather?.main.temp || 28)}
                  <span className="weather-forest-temp-unit">°C</span>
                </div>
                <p className="weather-forest-temp-range">
                  H: {Math.round(currentWeather?.main.temp_max || 32)}° L: {Math.round(currentWeather?.main.temp_min || 22)}°
                </p>
              </div>
              <div className="weather-forest-icon-section">
                <div className="weather-forest-icon">
                  {getWeatherIcon(currentWeather?.weather[0].main || "Rain")}
                </div>
                <div className="weather-forest-condition">
                  {currentWeather?.weather[0].description || "Light rain shower"}
                </div>
              </div>
            </div>

            <div className="weather-forest-details">
              <div className="weather-forest-detail">
                <div className="weather-forest-detail-value">
                  Wind: {Math.round((currentWeather?.wind.speed || 2.1) * 3.6)} km/h
                </div>
                <div className="weather-forest-detail-label">Wind Speed</div>
              </div>
              <div className="weather-forest-detail">
                <div className="weather-forest-detail-value">
                  Humidity: {currentWeather?.main.humidity || 65}%
                </div>
                <div className="weather-forest-detail-label">Humidity</div>
              </div>
            </div>

            {/* 2-Day Forecast */}
            {/* {forecast.length > 0 && (
              <div className="weather-forest-forecast">
                <div className="weather-forest-forecast-title">Next 2 Days</div>
                <div className="weather-forest-forecast-grid">
                  {forecast.map((day, i) => (
                    <div key={i} className="weather-forest-forecast-day">
                      <div className="weather-forest-forecast-date">
                        {formatDate(day.dt)}
                      </div>
                      <div className="weather-forest-forecast-icon">
                        {getWeatherIcon(day.weather[0].main)}
                      </div>
                      <div className="weather-forest-forecast-temp">
                        {Math.round(day.main.temp)}°C
                      </div>
                      <div className="weather-forest-forecast-condition">
                        {day.weather[0].description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}
          </div>
        </>
      )}

      <style jsx>{`
        .weather-forest-container {
          width: 100%;
          height: 100%;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(34, 139, 34, 0.2), 0 12px 28px rgba(0, 0, 0, 0.12);
          background: linear-gradient(135deg, #2E7D32 0%, #388E3C 25%, #4CAF50 50%, #66BB6A 75%, #81C784 100%);
          color: white;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
          display: flex;
          flex-direction: column;
          background-image: url('assets/images/trees.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        .weather-forest-container::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background:
            radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.12) 0%, transparent 50%),
            linear-gradient(135deg, rgba(0,0,0,0.2) 0%, transparent 100%);
          pointer-events: none;
        }

        .weather-forest-container * {
          font-weight: bold !important;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.9), 0 0 8px rgba(0, 0, 0, 0.5) !important;
        }

        .weather-forest-header { padding: 1rem 1.5rem 0.5rem; border-bottom: 2px solid rgba(255,255,255,0.15); z-index: 2; }
        .weather-forest-title { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .weather-forest-location { font-size: 1.2rem; font-weight: 800; margin: 0; }
        .weather-forest-date { font-size: 1.1rem; opacity: 0.95; }

        .weather-forest-main { padding: 0.5rem 1.5rem; flex: 1; z-index: 2; display: flex; flex-direction: column; justify-content: center; }
        .weather-forest-current { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
        .weather-forest-temp-section { flex: 1; }
        .weather-forest-temp { font-size: 2.5rem; margin: 0; line-height: 1; display: flex; align-items: flex-start; }
        .weather-forest-temp-unit { font-size: 1.2rem; margin-top: 0.4rem; margin-left: 0.4rem; opacity: 0.9; }
        .weather-forest-temp-range { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0; }

        .weather-forest-icon-section { text-align: center; margin-left: 1.5rem; display: flex; flex-direction: column; align-items: center; }
        .weather-forest-icon { font-size: 3rem; filter: drop-shadow(3px 3px 8px rgba(0,0,0,0.8)); }
        .weather-forest-condition { font-size: 0.9rem; opacity: 0.95; text-transform: capitalize; }

        .weather-forest-details {
          display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem;
          padding: 0.5rem; background: rgba(255,255,255,0.12); border-radius: 16px;
          backdrop-filter: blur(15px); border: 2px solid rgba(255,255,255,0.15);
        }
        .weather-forest-detail { text-align: center; }
        .weather-forest-detail-value { font-size: 1.2rem; }
        .weather-forest-detail-label { opacity: 0.85; font-size: 0.85rem; }

        .weather-forest-message {
          background: rgba(255,255,255,0.18); border-radius: 20px; padding: 1rem;
          margin: 1rem 1.5rem 0; backdrop-filter: blur(20px); border: 2px solid rgba(255,255,255,0.15);
        }
        .weather-forest-message-text { font-size: 1rem; line-height: 1.4; margin: 0; text-align: center; }

        .weather-forest-error {
          background: rgba(255,255,255,0.2); border: 2px solid rgba(255,255,255,0.25);
          border-radius: 16px; padding: 1.5rem; margin: 1.5rem 2rem; font-size: 1.1rem; text-align: center;
        }

        .weather-forest-loading {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          min-height: 300px; font-size: 1.2rem; opacity: 0.95;
        }
        .weather-forest-spinner {
          width: 30px; height: 30px; border: 3px solid rgba(255,255,255,0.3);
          border-top: 3px solid white; border-radius: 50%; animation: spin 1s linear infinite;
          margin-bottom: 1rem; filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.5));
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Forecast Styles */
        .weather-forest-forecast { margin-top: 1rem; }
        .weather-forest-forecast-title { font-size: 1.1rem; margin-bottom: 0.8rem; opacity: 0.9; }
        .weather-forest-forecast-grid { display: flex; gap: 1rem; }
        .weather-forest-forecast-day { flex: 1; text-align: center; background: rgba(255,255,255,0.12); padding: 0.8rem; border-radius: 12px; }
        .weather-forest-forecast-date { font-size: 0.9rem; margin-bottom: 0.4rem; }
        .weather-forest-forecast-icon { font-size: 1.8rem; margin: 0.4rem 0; }
        .weather-forest-forecast-temp { font-size: 1.1rem; font-weight: bold; }
        .weather-forest-forecast-condition { font-size: 0.8rem; opacity: 0.9; text-transform: capitalize; }

        @media (max-width: 768px) {
          .weather-forest-container { min-height: 420px; border-radius: 20px; }
          .weather-forest-temp { font-size: 3rem; }
          .weather-forest-icon { font-size: 3.2rem; }
          .weather-forest-location { font-size: 1.2rem; }
          .weather-forest-message-text { font-size: 1rem; }
        }

        @media (max-width: 576px) {
          .weather-forest-temp { font-size: 2.8rem; }
          .weather-forest-icon { font-size: 2.8rem; }
          .weather-forest-details { gap: 1rem; padding: 1rem; }
          .weather-forest-forecast-grid { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default WeatherComponent;